import React, {useCallback, useEffect} from 'react';
import css from './ReportsV2List.module.scss';
import {Report} from '../../../types/Api/Reports';
import {Link} from 'react-router-dom';
import {IconHeart} from '../../../system/icons/IconHeart';
import moment from 'moment';
import {ReportsV2ListIntensity} from './ReportsV2ListIntensity/ReportsV2ListIntensity';

import {Spikes2Icon} from '../icons/Spikes2Icon';
import {Spikes3Icon} from '../icons/Spikes3Icon';
import {SkeletonLoader} from '../../../system/SkeletonLoader/SkeletonLoader';
import classNames from 'classnames';
import {buildRoute} from '../../navigation/navigationUtils';
import {DASHBOARD_ROUTES} from '../../navigation/dashboard/DashboardRoutes';
import {Spikes4Icon} from '../icons/Spikes4Icon';

type ReportsV2ListProps = {
  reports: Report[];
  onLoadMore?: () => Promise<void>;
};

const INFINITE_SCROLL_THRESHOLD = 100;

const getScrollParent = (node: HTMLElement | null) => {
  if (!node) {
    return null;
  }

  const overflowY = window.getComputedStyle(node).overflowY;

  if (overflowY !== 'visible' && overflowY !== 'hidden') {
    return node;
  }

  return getScrollParent(node.parentElement);
};

export const ReportsV2List = ({reports, onLoadMore}: ReportsV2ListProps) => {
  const scrollParentRef = React.useRef<HTMLDivElement | null>(null);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const handleLazyLoadScroll = useCallback(
    async (e: Event) => {
      const target = e.target as HTMLDivElement;

      if (
        target.scrollHeight - target.scrollTop - target.clientHeight <
          INFINITE_SCROLL_THRESHOLD &&
        !isLoadingMore &&
        onLoadMore
      ) {
        setIsLoadingMore(true);
        try {
          await onLoadMore();
        } catch (e) {
          console.error(e);
        }
        setIsLoadingMore(false);
      }
    },
    [onLoadMore, isLoadingMore]
  );

  useEffect(() => {
    return () => {
      if (scrollParentRef.current) {
        scrollParentRef.current.removeEventListener(
          'scroll',
          handleLazyLoadScroll
        );
      }
    };
  }, [handleLazyLoadScroll]);

  return (
    <div
      className={css.reportsList}
      ref={(el) => {
        scrollParentRef.current = getScrollParent(el);

        if (scrollParentRef.current) {
          scrollParentRef.current.addEventListener(
            'scroll',
            handleLazyLoadScroll
          );
        }
      }}
    >
      {reports.map((report) => (
        <ReportV2ListItem key={report.id} report={report} />
      ))}
      {isLoadingMore && (
        <div className={css.reportsListLoader}>
          <ReportV2ListItemLoader />
        </div>
      )}
    </div>
  );
};

export const ReportsV2ListLoader = () => {
  return (
    <div className={css.reportsList}>
      <ReportV2ListItemLoader />
      <ReportV2ListItemLoader />
      <ReportV2ListItemLoader />
    </div>
  );
};

const ReportV2ListItemLoader = () => {
  return (
    <div className={classNames(css.report, css.reportLoadingSkeleton)}>
      <SkeletonLoader className={css.reportThumbnail} type="rect" />

      <div className={css.reportContent}>
        <SkeletonLoader type="title" className={css.reportTitle} />
        <SkeletonLoader type="text" className={css.reportSubtitle} />

        <div className={css.reportStats}>
          <SkeletonLoader
            type="text"
            widthFromTo={[30, 80]}
            className={css.reportSpikesStats}
          />
          <SkeletonLoader
            type="text"
            widthFromTo={[30, 80]}
            className={css.reportHeartRateStats}
          />
          <SkeletonLoader
            type="text"
            widthFromTo={[30, 80]}
            className={css.reportIntensityStats}
          />
        </div>
      </div>
    </div>
  );
};

const spikesQtyToIcon = [
  null,
  null,
  <Spikes2Icon />,
  <Spikes3Icon />,
  <Spikes4Icon />,
];

const ReportV2ListItem = ({report}: {report: Report}) => {
  return (
    <Link
      to={buildRoute({
        path: DASHBOARD_ROUTES.REPORTS_VIEW,
        params: {id: report.slug},
        query: document.location.search,
      })}
      className={css.reportLinkWrapper}
    >
      <div className={css.report}>
        <div
          className={css.reportThumbnail}
          style={{
            backgroundImage: `url(${report.thumbnailUrl})`,
          }}
        >
          <div className={css.reportTime}>
            {moment.duration(-moment().diff(moment(report.to))).humanize(true)}
          </div>
        </div>

        <div className={css.reportContent}>
          <div className={css.reportTitle}>{report.title}</div>

          <div className={css.reportSubtitle}>
            {report.games
              .slice(0, 3)
              .map((game) => game.name)
              .join(' • ')}
          </div>

          <div className={css.reportStats}>
            <div className={css.reportSpikesStats}>
              {spikesQtyToIcon[report.statistics.spikes] || spikesQtyToIcon[3]}
              <span className={css.reportSpikesStatsTextValue}>
                {report.statistics.spikes}
              </span>
              <span className={css.reportSpikesStatsText}>Spikes</span>
            </div>

            <div className={css.reportHeartRateStats}>
              <IconHeart size={12} className={css.reportHeartRateStatsIcon} />
              <div className={css.reportHeartRateStatsText}>
                <span className={css.reportHeartRateStatsValue}>
                  {`${report.statistics.avg}–${report.statistics.max}`}
                </span>
                {' BPM'}
              </div>
            </div>

            <ReportsV2ListIntensity intensity={report.statistics.intensity} />
          </div>
        </div>
      </div>
    </Link>
  );
};
